'use client';

import { configKeepData5Mins } from '@/config/useQuery';
import { api } from '@/trpc/client';
import { Star } from 'lucide-react';
import { useTranslations } from 'next-intl';

// ─── Star display ─────────────────────────────────────────────────────────────

const StarRow = ({ value, max = 5 }: { value: number; max?: number }) => (
    <div className='flex items-center gap-0.5'>
        {Array.from({ length: max }).map((_, i) => {
            const fill = value - i;
            return (
                <Star
                    key={i}
                    size={14}
                    className={
                        fill >= 1
                            ? 'fill-amber-400 text-amber-400'
                            : fill > 0
                              ? 'fill-amber-200 text-amber-400'
                              : 'fill-gray-100 text-gray-200'
                    }
                />
            );
        })}
    </div>
);

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonBlock = ({ className }: { className?: string }) => (
    <div className={`animate-pulse rounded bg-gray-100 ${className ?? ''}`} />
);

// ─── Main component ───────────────────────────────────────────────────────────

const PublicRatingSection = () => {
    const transPage = useTranslations('PublicStatistics');

    const { data: summaryData, isLoading: isSummaryLoading } =
        api.dashboard.Query_GetPublicDossierRatingSummary.useQuery(
            {},
            configKeepData5Mins
        );

    const { data: reviewsData, isLoading: isReviewsLoading } =
        api.dashboard.Query_GetPublicDossierRatingReviews.useQuery(
            { pageSize: 4 },
            configKeepData5Mins
        );

    const summary = summaryData?.data ?? null;
    const reviews = reviewsData?.data ?? [];

    const totalRatings = summary?.TotalRatings ?? 0;
    const averageScore = summary?.AverageScore ?? 0;
    const satisfiedRate = summary?.SatisfiedRate ?? 0;
    const distribution = summary?.Distribution ?? [];

    const starLabels = [
        transPage('PublicStatistics_Rating5'),
        transPage('PublicStatistics_Rating4'),
        transPage('PublicStatistics_Rating3'),
        transPage('PublicStatistics_Rating2'),
        transPage('PublicStatistics_Rating1'),
    ];

    return (
        <div className='overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100'>
            {/* Header */}
            <div className='flex items-center justify-between border-b border-gray-50 px-5 py-3.5'>
                <p className='text-sm font-semibold text-gray-700'>
                    {transPage('PublicStatistics_ServiceRating')}
                </p>
            </div>

            <div className='p-5'>
                <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                    {/* ── Left: score + distribution ── */}
                    <div className='space-y-5'>
                        {/* Overall score */}
                        {isSummaryLoading ? (
                            <div className='flex items-center gap-5'>
                                <SkeletonBlock className='h-[76px] w-[88px] rounded-2xl' />
                                <div className='flex-1 space-y-2'>
                                    <SkeletonBlock className='h-4 w-28' />
                                    <SkeletonBlock className='h-4 w-36' />
                                    <SkeletonBlock className='h-4 w-32' />
                                </div>
                            </div>
                        ) : (
                            <div className='flex items-center gap-5'>
                                <div className='flex flex-col items-center justify-center rounded-2xl bg-amber-50 px-6 py-4'>
                                    <span className='text-4xl font-bold tabular-nums text-amber-500'>
                                        {averageScore.toFixed(1)}
                                    </span>
                                    <span className='mt-0.5 text-xs text-amber-400'>
                                        {transPage(
                                            'PublicStatistics_RatingOutOf'
                                        )}
                                    </span>
                                </div>
                                <div className='space-y-1'>
                                    <StarRow value={averageScore} />
                                    <p className='text-sm text-gray-500'>
                                        <span className='font-semibold text-gray-800'>
                                            {totalRatings.toLocaleString(
                                                'vi-VN'
                                            )}
                                        </span>{' '}
                                        {transPage(
                                            'PublicStatistics_RatingTotal'
                                        )}
                                    </p>
                                    <p className='text-sm text-gray-500'>
                                        <span className='font-semibold text-emerald-600'>
                                            {satisfiedRate.toFixed(1)}%
                                        </span>{' '}
                                        {transPage(
                                            'PublicStatistics_RatingSatisfied'
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Distribution bars */}
                        {isSummaryLoading ? (
                            <div className='space-y-2'>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <SkeletonBlock
                                        key={i}
                                        className='h-5 w-full'
                                    />
                                ))}
                            </div>
                        ) : !isSummaryLoading && totalRatings === 0 ? (
                            <p className='text-sm text-gray-400'>
                                {transPage('PublicStatistics_NoReviews')}
                            </p>
                        ) : (
                            <div className='space-y-2'>
                                {distribution.map((d, i) => {
                                    const barColor =
                                        d.Star >= 4
                                            ? 'bg-emerald-400'
                                            : d.Star === 3
                                              ? 'bg-amber-300'
                                              : 'bg-red-400';
                                    return (
                                        <div
                                            key={d.Star}
                                            className='flex items-center gap-2 text-xs'
                                        >
                                            <span className='w-24 shrink-0 text-gray-500'>
                                                {starLabels[i]}
                                            </span>
                                            <div className='h-2 flex-1 overflow-hidden rounded-full bg-gray-100'>
                                                <div
                                                    className={`h-full rounded-full ${barColor} transition-all`}
                                                    style={{
                                                        width: `${d.Pct}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className='w-8 shrink-0 text-right tabular-nums text-gray-400'>
                                                {d.Pct.toFixed(1)}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* ── Right: recent reviews ── */}
                    <div className='space-y-3'>
                        <p className='text-xs font-semibold uppercase tracking-wider text-gray-400'>
                            {transPage('PublicStatistics_RecentReviews')}
                        </p>

                        {isReviewsLoading ? (
                            <div className='space-y-3'>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <SkeletonBlock
                                        key={i}
                                        className='h-[84px] w-full rounded-xl'
                                    />
                                ))}
                            </div>
                        ) : reviews.length === 0 ? (
                            <p className='text-sm text-gray-400'>
                                {transPage('PublicStatistics_NoReviews')}
                            </p>
                        ) : (
                            <div className='space-y-3'>
                                {reviews.map((r, idx) => (
                                    <div
                                        key={idx}
                                        className='rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3'
                                    >
                                        <div className='mb-1.5 flex items-center justify-between gap-2'>
                                            <StarRow value={r.RatingScore} />
                                            <span className='text-[11px] text-gray-400'>
                                                {new Date(
                                                    r.SubmittedAt
                                                ).toLocaleDateString('vi-VN', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                })}
                                            </span>
                                        </div>
                                        <p className='text-sm leading-relaxed text-gray-700'>
                                            {r.Feedback}
                                        </p>
                                        {r.ReviewTitle && (
                                            <p className='mt-1.5 text-[11px] text-gray-400'>
                                                <span className='font-medium text-gray-500'>
                                                    {transPage(
                                                        'PublicStatistics_ReviewTitle'
                                                    )}
                                                    :
                                                </span>{' '}
                                                {r.ReviewTitle}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicRatingSection;
